import { useEffect } from "react";
import { base44 } from "@/api/base44Client";

// Inyecta dinámicamente las meta etiquetas configuradas desde el panel admin
// (sección Search Console) en el <head> de la página. Lee la entidad
// SearchConsoleConfig (lectura pública vía RLS) y sobrescribe las meta tags
// estáticas del index.html con los valores personalizados del admin.
//
// Las meta tags de verificación (google-site-verification, msvalidate.01, etc.)
// se inyectan para que Google/Bing/Yandex verifiquen la propiedad del dominio.
// Las meta tags Open Graph y Twitter Card mejoran el preview al compartir en
// redes sociales. El title y canonical también se actualizan.

const META_MAP = [
  { key: "meta_description", attr: "name", val: "description" },
  { key: "meta_keywords", attr: "name", val: "keywords" },
  { key: "robots", attr: "name", val: "robots" },
  { key: "author", attr: "name", val: "author" },
  { key: "language", attr: "name", val: "language" },
  { key: "revisit_after", attr: "name", val: "revisit-after" },
  { key: "google_site_verification", attr: "name", val: "google-site-verification" },
  { key: "msvalidate_01", attr: "name", val: "msvalidate.01" },
  { key: "yandex_verification", attr: "name", val: "yandex-verification" },
  { key: "p_domain_verify", attr: "name", val: "p:domain_verify" },
  { key: "alexa_verify_id", attr: "name", val: "alexaVerifyID" },
  { key: "norton_safeweb_site_verification", attr: "name", val: "norton-safeweb-site-verification" },
  { key: "geo_region", attr: "name", val: "geo.region" },
  { key: "geo_placename", attr: "name", val: "geo.placename" },
  { key: "geo_position", attr: "name", val: "geo.position" },
  { key: "icbm", attr: "name", val: "ICBM" },
  { key: "theme_color", attr: "name", val: "theme-color" },
  { key: "og_title", attr: "property", val: "og:title" },
  { key: "og_description", attr: "property", val: "og:description" },
  { key: "og_image", attr: "property", val: "og:image" },
  { key: "og_url", attr: "property", val: "og:url" },
  { key: "og_type", attr: "property", val: "og:type" },
  { key: "og_site_name", attr: "property", val: "og:site_name" },
  { key: "og_locale", attr: "property", val: "og:locale" },
  { key: "twitter_card", attr: "name", val: "twitter:card" },
  { key: "twitter_title", attr: "name", val: "twitter:title" },
  { key: "twitter_description", attr: "name", val: "twitter:description" },
  { key: "twitter_image", attr: "name", val: "twitter:image" },
  { key: "twitter_site", attr: "name", val: "twitter:site" },
  { key: "twitter_creator", attr: "name", val: "twitter:creator" }
];

let cachedConfig = null;
let fetchPromise = null;

async function fetchConfig() {
  if (cachedConfig) return cachedConfig;
  if (fetchPromise) return fetchPromise;
  fetchPromise = (async () => {
    try {
      const list = await base44.entities.SearchConsoleConfig.list("-updated_date", 1);
      cachedConfig = (list && list[0]) || null;
    } catch {
      cachedConfig = null;
    }
    return cachedConfig;
  })();
  return fetchPromise;
}

// Invalida la caché de config SEO. Llamar tras guardar cambios en el panel
// admin (SearchConsoleSection) para que la próxima carga relea la config y los
// usuarios existentes vean las meta tags actualizadas sin recargar.
export function invalidateSearchConsoleCache() {
  cachedConfig = null;
  fetchPromise = null;
}

export async function getSearchConsoleConfig() {
  return await fetchConfig();
}

export async function getVerificationFile(filename) {
  const cfg = await fetchConfig();
  if (!cfg || !cfg.verification_files) return null;
  try {
    const files = JSON.parse(cfg.verification_files);
    return Array.isArray(files) ? files.find(f => f.filename === filename) : null;
  } catch {
    return null;
  }
}

function setMeta(attr, name, content) {
  if (!content || String(content).trim() === "") return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
}

function setLink(rel, href, extraAttrs) {
  if (!href || String(href).trim() === "") return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", String(href));
  if (extraAttrs) for (const [k, v] of Object.entries(extraAttrs)) el.setAttribute(k, v);
}

function setCustomHtml(html) {
  if (!html || String(html).trim() === "") return;
  let el = document.getElementById("bhgc-custom-head");
  if (!el) {
    el = document.createElement("div");
    el.id = "bhgc-custom-head";
    el.style.display = "none";
    document.head.appendChild(el);
  }
  el.innerHTML = html;
}

// Inyecta JSON-LD (schema.org) para el knowledge graph de Google: Organization
// (datos de la empresa) y WebSite con SearchAction (sitelinks searchbox).
// Mejora el posicionamiento de marca y el branding en resultados de búsqueda.
function setJsonLd(id, obj) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(obj);
}

export default function SeoHeadInjector() {
  useEffect(() => {
    let mounted = true;
    (async () => {
      const cfg = await fetchConfig();
      if (!mounted || !cfg || !cfg.is_active) return;
      // Title
      if (cfg.title) document.title = cfg.title;
      // Meta tags
      for (const m of META_MAP) {
        setMeta(m.attr, m.val, cfg[m.key]);
      }
      // Canonical: si el admin configuró canonical_url se usa esa; si no, se
      // deriva del host actual (window.location.origin) para que cada dominio
      // conectado (principal + personalizados) sirva su propio canonical en vez
      // del dominio principal hardcodeado en index.html, evitando contenido
      // duplicado en Google cuando hay múltiples dominios.
      const baseOrigin = (typeof window !== "undefined" ? window.location.origin : "");
      const baseUrl = cfg.canonical_url || baseOrigin;
      setLink("canonical", baseUrl);
      // hreflang y og:url dinámicos según el dominio actual (principal o
      // personalizado de franquicia). Sustituye los hardcodeados en index.html
      // para evitar contenido duplicado en Google cuando hay múltiples dominios.
      setLink("alternate", baseUrl, { hreflang: "es-mx" });
      // x-default apunta al mismo origen (cada dominio es canónico de sí mismo).
      const xdef = document.head.querySelector('link[rel="alternate"][hreflang="x-default"]');
      if (xdef) xdef.setAttribute("href", baseUrl);
      const ogUrl = document.head.querySelector('meta[property="og:url"]');
      if (ogUrl) ogUrl.setAttribute("content", baseUrl);
      // Custom head HTML
      setCustomHtml(cfg.custom_head_html);
      // JSON-LD de marca: Organization (knowledge graph) + WebSite (sitelinks
      // searchbox). Refuerza el branding y el posicionamiento de marca en Google.
      const siteName = cfg.title || "BHGC Nexus Logistics";
      setJsonLd("bhgc-org", {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: siteName,
        url: baseUrl,
        ...(cfg.og_image ? { logo: cfg.og_image, image: cfg.og_image } : {}),
        description: cfg.meta_description || undefined,
        areaServed: "MX",
        knowsLanguage: "es-MX"
      });
      setJsonLd("bhgc-website", {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: siteName,
        url: baseUrl,
        inLanguage: "es-MX",
        potentialAction: {
          "@type": "SearchAction",
          target: `${baseUrl}/blog?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      });
    })();
    return () => { mounted = false; };
  }, []);
  return null;
}